import {
	type ClientPortfolioDetailBankProductToolResponseDTO,
	type ClientPortfolioDetailBondToolResponseDTO,
	type ClientPortfolioDetailCommodityToolResponseDTO,
	type ClientPortfolioDetailCryptocurrencyToolResponseDTO,
	type ClientPortfolioDetailFundToolResponseDTO,
	type ClientPortfolioDetailToolsResponseDTO,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { InvestmentToolInput } from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolSchema";
import { getCommodityLabel } from "web_myplann/Portfolio/Forms/ToolsShared/getCommodityLabel";
import { mapCryptoInstructionToForm } from "web_myplann/Portfolio/Forms/ToolsShared/mapCryptoInstruction";
import { mapInstructionToForm } from "web_myplann/Portfolio/Forms/ToolsShared/mapInstruction";
import { mapToolManualValuesToForm } from "web_myplann/Portfolio/PortfolioShared/ManualValue/mapToolManualValuesToForm";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";

const withCount = <T>(instruction: T) => ({ ...instruction, count: null });

const toIdentificationType = (
	value: ClientPortfolioToolIdentificationTypeEnum | null | undefined,
) =>
	value === ClientPortfolioToolIdentificationTypeEnum.Other
		? ClientPortfolioToolIdentificationTypeEnum.Other
		: ClientPortfolioToolIdentificationTypeEnum.Isin;

const mapFundToForm = (
	tool: ClientPortfolioDetailFundToolResponseDTO,
): InvestmentToolInput => ({
	...mapToolManualValuesToForm(tool.manualValues),
	isAdvisorBond: false,
	id: tool.id,
	type: tool.type ?? ClientPortfolioToolTypeEnum.Fund,
	identificationType: toIdentificationType(tool.identificationType),
	identification: tool.identification ?? "",
	holdingType: null,
	name: tool.name ?? "",
	instructions: tool.instructions.map((instruction) =>
		withCount(mapInstructionToForm(instruction)),
	),
});

const mapBondToForm = (
	tool: ClientPortfolioDetailBondToolResponseDTO,
): InvestmentToolInput => ({
	...mapToolManualValuesToForm(tool.manualValues),
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Bond,
	identificationType: toIdentificationType(tool.identificationType),
	identification: tool.identification ?? "",
	holdingType: null,
	name: tool.name ?? "",
	isAdvisorBond: true,
	instructions: [],
});

const mapCommodityToForm = (
	tool: ClientPortfolioDetailCommodityToolResponseDTO,
	t: TranslateFunction,
): InvestmentToolInput => ({
	...mapToolManualValuesToForm(tool.manualValues),
	isAdvisorBond: false,
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Commodity,
	identificationType: null,
	identification: getCommodityLabel(tool.identification ?? "", t),
	holdingType: null,
	name: tool.name ?? "",
	instructions: tool.commodities.map((instruction) =>
		withCount(mapInstructionToForm(instruction)),
	),
});

const mapCryptoToForm = (
	tool: ClientPortfolioDetailCryptocurrencyToolResponseDTO,
): InvestmentToolInput => ({
	...mapToolManualValuesToForm(tool.manualValues),
	isAdvisorBond: false,
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.Crypto,
	identificationType: null,
	identification: tool.identification ?? "",
	holdingType: tool.holdingType ?? null,
	name: tool.name ?? "",
	instructions: tool.cryptocurrencyInstructions.map(mapCryptoInstructionToForm),
});

const mapBankProductToForm = (
	tool: ClientPortfolioDetailBankProductToolResponseDTO,
): InvestmentToolInput => ({
	...mapToolManualValuesToForm(tool.manualValues),
	isAdvisorBond: false,
	id: tool.id,
	type: ClientPortfolioToolTypeEnum.BankProduct,
	identificationType: null,
	identification: "",
	holdingType: null,
	name: tool.name ?? "",
	instructions: tool.instructions.map((instruction) =>
		withCount(mapInstructionToForm(instruction)),
	),
});

export const mapInvestmentToolsToForm = (
	tools: ClientPortfolioDetailToolsResponseDTO | null | undefined,
	t: TranslateFunction,
): InvestmentToolInput[] => {
	if (!tools) {
		return [];
	}

	return [
		...tools.funds.map(mapFundToForm),
		...tools.bonds.map(mapBondToForm),
		...tools.commodities.map((tool) => mapCommodityToForm(tool, t)),
		...tools.cryptocurrencies.map(mapCryptoToForm),
		...tools.bankProducts.map(mapBankProductToForm),
	];
};
